// AdvisorConsole — the always-on Strategic Advisor S2S console at /advisor (W9).
//
// An ElevenLabs Agents conversation over WebRTC fronts the user's 10x10
// portfolio. The Strategic Advisor (Delta voice) has broad READ access to the
// Memory Highway and delegates engineering work to the Head of Engineering,
// which dispatches background Claude Code jobs over the /control WS
// (ControlChannel, W7). Exactly ONE live Conversation owns the mic at a time.
//
// USER ADDRESSING MODEL: users summon a specialist by ASKING the active
// persona ("bring in the Head of Engineering" -> summon_specialist tool;
// "send me back to the advisor" -> return_to_advisor tool). There is NO
// hotword routing in v1 — the active persona's client toolset is the only
// path that arms a handoff.
//
// HANDOFF (one-shot, two triggers; the swap NEVER runs synchronously inside a
// tool call): summon_specialist/return_to_advisor call onSummon/onReturn
// synchronously, which prefetch the next persona's /s2s/token at ARM time and
// arm pendingHandoffRef + a 3500ms fallback timer, then return their (now <=1
// short sentence) spoken pre-handoff line. executeHandoff fires on the FIRST of
// (a) the next onModeChange 'listening' AFTER the ref was set (the SDK reports
// 'speaking' while voicing the line, 'listening' when playback ends — that
// transition is the spoken-line-finished signal), or (b) the 3.5s fallback
// timer. It reads-and-nulls the ref (one-shot guard), sets intentionalEndRef
// (which suppresses the S2S auto-restart), then endSession -> startConversation
// with the prefetched token, a per-session firstMessage override (so the new
// persona never replays its canned greeting) and a '[handoff-brief]' kickoff
// user-message that makes the specialist answer the user's pending question
// unprompted. handoff_context (briefing + recent transcript + pending request)
// rides in via dynamicVariables. The swap gap is masked by the persona's line.
//
// LATENCY BUDGET (enforced in code review): mic->edge <=80ms (WebRTC),
// ASR+turn-decision <=400ms, LLM TTFT <=500ms, TTS first byte <=150ms,
// playback start <=50ms => voice-to-voice p50 <=1.2s, p90 <=2.0s. Client tool
// budgets: memory reads <=250ms (in-memory STM), control reads <=150ms
// (localhost), dispatch ack <=100ms. NO tool may await job completion or
// unbounded network. Barge-in is platform-native — there is NO client pause
// logic and the mic is NEVER ducked while jobs run. LATENCY CUTS: the handoff
// /s2s/token is prefetched at arm time (overlapping the spoken pre-handoff
// line), the repo cache is warmed at connect() so the first repo tool resolves
// instantly, and the first unintentional-reconnect retry is 500ms.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Conversation } from '@elevenlabs/client';
import { ref, get } from 'firebase/database';
import { auth, database, createRealtimeListener } from '../firebase';
import MemoryHighway from '../memory/MemoryHighway';
import ControlChannel from '../lib/agentControl';
import { resolveAgentBackend } from '../lib/agentBackend';
import { buildClientTools, warmRepoCache } from '../agents/personas';
import useStickToBottom from './useStickToBottom';
import './AdvisorConsole.css';

// Backend URL is auto-derived from where the app is loaded (local loopback vs the
// tailnet :8443 proxy) — see ../lib/agentBackend.
const ENV_TOKEN = process.env.REACT_APP_AGENT_TOKEN || '';

// Handoff fallback ceiling — executeHandoff fires no later than this after the
// summon/return tool arms the ref, even if the spoken-line-finished signal
// (onModeChange 'listening') never arrives. The spoken pre-handoff line is now
// <=1 short sentence (~2-3s), so 3.5s comfortably covers it.
const HANDOFF_FALLBACK_MS = 3500;

// S2S auto-restart backoff (per leg) for UNINTENTIONAL disconnects.
const S2S_RESTART_DELAYS_MS = [500, 2000, 5000];

// Synthetic kickoff user-message prefix — fired right after a handoff session
// connects so the new persona answers the pending request unprompted. Filtered
// out of the transcript pane and the CL (never a fabricated USER entry).
const KICKOFF_PREFIX = '[handoff-brief]';

// Throttle floor for sendContextualUpdate — keeps background-job narration from
// flooding the persona (>=3s apart).
const CONTEXTUAL_THROTTLE_MS = 3000;

const ACTIVITY_CAP = 200;

const PERSONA_LABELS = {
  'strategic-advisor': 'Strategic Advisor',
  'head-of-engineering': 'Head of Engineering',
};

export default function AdvisorConsole() {
  const uid = auth.currentUser?.uid;

  // ---- config (shares localStorage keys with RepoConsole so /coder settings carry over) ----
  const [backendUrl, setBackendUrl] = useState(
    () => resolveAgentBackend(localStorage.getItem('agentBackendUrl'))
  );
  const [token, setToken] = useState(() => localStorage.getItem('agentToken') || ENV_TOKEN);

  // ---- data / identity ----
  const [projects, setProjects] = useState([]);
  const [profileName, setProfileName] = useState('');
  const [memoryStatus, setMemoryStatus] = useState('loading'); // 'loading'|'ready'

  // ---- conversation state machine ----
  // 'idle' | 'connecting' | 'strategic-advisor' | 'head-of-engineering'
  const [personaState, setPersonaState] = useState('idle');
  const [s2sStatus, setS2sStatus] = useState('idle'); // dot: idle|connecting|connected|reconnecting|failed
  const [controlStatus, setControlStatus] = useState('idle');
  const [muted, setMuted] = useState(false);
  const [showRestart, setShowRestart] = useState(false);
  const [status, setStatus] = useState('Idle');

  // ---- panes ----
  const [transcript, setTranscript] = useState([]); // {who:'you'|'agent', persona, text}
  const [activity, setActivity] = useState({ items: [], total: 0 }); // items:{label, text}[]
  const [jobs, setJobs] = useState([]); // StatusEntry[] from list()+session-events
  const [perms, setPerms] = useState([]); // {id, sessionId, label, command, title, reason}

  const convScroll = useStickToBottom(transcript.length);
  const actScroll = useStickToBottom(activity.total);

  // ---- refs ----
  const memoryRef = useRef(null);
  const projectsRef = useRef([]);
  const profileNameRef = useRef('');
  const controlRef = useRef(null);
  const conversationRef = useRef(null);
  const personaStateRef = useRef('idle');

  // Handoff machinery.
  const pendingHandoffRef = useRef(null); // { target, context, timer } | null
  const intentionalEndRef = useRef(false);

  // S2S reconnect bookkeeping.
  const s2sRestartAttemptsRef = useRef(0);
  const s2sRestartTimerRef = useRef(null);
  const startConversationRef = useRef(null);

  // Contextual-update throttle (>=3s apart).
  const lastContextualRef = useRef(0);

  const unmountedRef = useRef(false);

  // Keep refs mirrored so async callbacks read the latest values.
  useEffect(() => {
    personaStateRef.current = personaState;
  }, [personaState]);
  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);
  useEffect(() => {
    profileNameRef.current = profileName;
  }, [profileName]);

  const persist = (k, v) => localStorage.setItem(k, v);

  const getProjects = useCallback(() => projectsRef.current, []);

  // Live MemoryHighway getter — client tools read the CURRENT instance per call,
  // never a value captured at session start (which would be null before memory
  // loads or stale after an additive update).
  const getMemory = useCallback(() => memoryRef.current, []);

  // Serialized jobs list for dynamicVariables / handoff context.
  const jobsSummary = useCallback(() => {
    const list = jobs;
    if (!list.length) return 'No active jobs.';
    return list
      .map(
        (s) =>
          `${s.label}: ${s.busy ? 'working' : 'idle'}${
            s.pendingPermissions ? ', awaiting on-screen approval' : ''
          }`
      )
      .join('; ');
  }, [jobs]);

  // Mint an ElevenLabs conversation token for a persona. Extracted so a handoff
  // can PREFETCH it at arm time (overlapping the spoken pre-handoff line) and so
  // startConversation can fall back to a fresh mint when a prefetch rejects.
  const fetchS2SToken = useCallback(
    async (persona) => {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(`${backendUrl.trim()}/s2s/token`, {
        method: 'POST',
        headers: {
          'x-agent-token': token.trim(),
          'content-type': 'application/json',
          ...(idToken ? { 'x-firebase-token': idToken } : {}),
        },
        body: JSON.stringify({ persona }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(`token ${res.status} ${detail}`.trim());
      }
      return (await res.json()).token;
    },
    [backendUrl, token]
  );

  // ===========================================================================
  // (1) Identity / data — projects listener + one-time profile fetch + memory.
  // ===========================================================================
  useEffect(() => {
    if (!uid) return undefined;

    // One-time profile read — users/{uid}/profile {name, vision, obstacles,
    // theme}. MemoryHighway.loadFromFirebase() does NOT load this node, so this
    // is the component's ONLY profile source (a READ, never a write). The same
    // snapshot would be reused for any other profile need (none in v1).
    get(ref(database, `users/${uid}/profile`))
      .then((snapshot) => {
        if (!unmountedRef.current) setProfileName(snapshot.val()?.name || '');
      })
      .catch(() => {
        /* profile read failure -> user_name falls back to 'there' */
      });

    // Projects listener — same object->array mapping as DeltaTrianglePage.js:114.
    const offProjects = createRealtimeListener(`users/${uid}/projects`, (snapshot) => {
      const data = snapshot.val();
      let parsed = [];
      if (data) {
        parsed = Object.keys(data)
          .map((id) => {
            const proj = data[id];
            return {
              id,
              name: proj.name || null,
              image: proj.image || null,
              parentId: proj.parentId || null,
              goals: Array.isArray(proj.goals) ? proj.goals : [],
              todos: Array.isArray(proj.todos) ? proj.todos : [],
            };
          })
          .filter((p) => p.name);
      }
      setProjects(parsed);
    });

    return () => {
      if (typeof offProjects === 'function') offProjects();
    };
  }, [uid]);

  // Keep MemoryHighway in sync with the projects listener. When an instance for
  // THIS uid already exists, update it ADDITIVELY (updateProjects swaps the
  // project list, loads ECB only for new ids, refreshes STM — never reloads
  // CL/PMB) instead of re-instantiating, so the live instance captured by no
  // one stays current and the CL tail survives mid-conversation snapshots. Only
  // a missing/foreign instance triggers the construct + loadFromFirebase path.
  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    if (memoryRef.current && memoryRef.current.userId === uid) {
      memoryRef.current
        .updateProjects(projects)
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setMemoryStatus('ready');
        });
      return () => {
        cancelled = true;
      };
    }
    setMemoryStatus('loading');
    const mem = new MemoryHighway(uid, projects);
    mem
      .loadFromFirebase()
      .then(() => {
        if (cancelled) return;
        memoryRef.current = mem;
        setMemoryStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        memoryRef.current = mem;
        setMemoryStatus('ready');
      });
    return () => {
      cancelled = true;
    };
  }, [uid, projects]);

  // ===========================================================================
  // (7) Background-job surfacing — driven by ControlChannel 'session-event'.
  // ===========================================================================

  // Throttled contextual update to the active persona (>=3s apart).
  const queueContextualUpdate = useCallback((text) => {
    const conv = conversationRef.current;
    if (!conv) return;
    const now = Date.now();
    if (now - lastContextualRef.current < CONTEXTUAL_THROTTLE_MS) return;
    lastContextualRef.current = now;
    try {
      conv.sendContextualUpdate(text);
    } catch {
      /* noop */
    }
  }, []);

  const pushActivity = useCallback((label, text) => {
    setActivity(({ items, total }) => ({
      items: [...items, { label, text }].slice(-ACTIVITY_CAP),
      total: total + 1,
    }));
  }, []);

  const handleSessionEvent = useCallback(
    ({ sessionId, label, payload }) => {
      if (!payload) return;
      const jobLabel = label || payload.label || sessionId;
      switch (payload.type) {
        case 'ready':
          // W4: every control job's seq-1 event. Visual only, never TTS'd.
          pushActivity(jobLabel, `${jobLabel} started in ${payload.repo?.name || '?'}`);
          break;
        case 'tool':
          pushActivity(jobLabel, `${payload.tool}: ${payload.summary || ''}`);
          break;
        case 'assistant-text':
          // Job text stays visual — the persona voice narrates, this never TTS's.
          pushActivity(jobLabel, payload.text);
          break;
        case 'permission-request':
          setPerms((p) =>
            p.some((x) => x.id === payload.id)
              ? p
              : [
                  ...p,
                  {
                    id: payload.id,
                    sessionId,
                    label: jobLabel,
                    command: payload.command,
                    title: payload.title,
                    reason: payload.reason,
                  },
                ]
          );
          queueContextualUpdate(
            'Background job ' +
              jobLabel +
              ' needs on-screen approval to run: ' +
              (payload.command || payload.title) +
              '. Briefly tell the user to review the Allow/Deny card.'
          );
          break;
        case 'result':
          queueContextualUpdate(
            'Background job ' +
              jobLabel +
              (payload.isError ? ' hit an error: ' : ' finished: ') +
              String(payload.text).slice(0, 200)
          );
          break;
        default:
          break;
      }
    },
    [pushActivity, queueContextualUpdate]
  );

  // ===========================================================================
  // (5) Handoff trigger mechanism.
  // ===========================================================================
  const executeHandoff = useCallback(async () => {
    // One-shot guard: read-and-null the ref and clear its timer. If already
    // null, a prior trigger consumed it — return immediately.
    const pending = pendingHandoffRef.current;
    pendingHandoffRef.current = null;
    if (!pending) return;
    if (pending.timer) clearTimeout(pending.timer);

    const conv = conversationRef.current;
    intentionalEndRef.current = true; // suppress the S2S auto-restart for this end
    try {
      if (conv) await conv.endSession();
    } catch {
      /* noop — onDisconnect will fire and the swap proceeds */
    }
    // Hand the prefetched token (minted at arm time) plus the firstMessage
    // override + kickoff to the new session.
    startConversationRef.current?.(pending.target, pending.context, {
      ...pending.opts,
      tokenPromise: pending.tokenPromise,
    });
  }, []);

  const armHandoff = useCallback(
    (target, context, opts = {}) => {
      // If a handoff is already pending, clear its timer first (last summon wins).
      if (pendingHandoffRef.current?.timer) clearTimeout(pendingHandoffRef.current.timer);
      // Prefetch the next persona's conversation token NOW so its ~round-trip
      // overlaps the spoken pre-handoff line. A rejection is swallowed here;
      // startConversation falls back to a fresh mint.
      const tokenPromise = fetchS2SToken(target);
      tokenPromise.catch(() => {});
      pendingHandoffRef.current = {
        target,
        context,
        opts,
        tokenPromise,
        timer: setTimeout(() => {
          executeHandoff();
        }, HANDOFF_FALLBACK_MS),
      };
    },
    [executeHandoff, fetchS2SToken]
  );

  // Called SYNCHRONOUSLY inside the summon_specialist client tool, before it
  // returns its spoken pre-handoff line. Builds a rich briefing (advisor line +
  // recent transcript + the user's pending request) and a kickoff user-message
  // so the specialist answers that pending request the moment it connects.
  const onSummon = useCallback(
    (specialist, briefing) => {
      const cl = memoryRef.current?.CL || [];
      const tail = cl
        .slice(-8)
        .map((c) => `${c.role}: ${c.content}`)
        .join('\n');
      const lastUser = [...cl].reverse().find((c) => c.role === 'USER')?.content || '';
      const context =
        `Strategic Advisor briefing: ${briefing}. Active jobs: ${jobsSummary()}. ` +
        `Recent conversation:\n${tail}`;
      armHandoff(specialist, context, {
        firstMessage: 'Head of Engineering — picking this up.',
        kickoff: lastUser
          ? `${KICKOFF_PREFIX} The user is waiting on: "${lastUser}". Answer or act on it now, speaking directly to the user. Do not mention this briefing message.`
          : undefined,
      });
    },
    [armHandoff, jobsSummary]
  );

  // Identical ref/trigger mechanism, target 'strategic-advisor'.
  const onReturn = useCallback(
    (summary) => {
      const cl = memoryRef.current?.CL || [];
      const tail = cl
        .slice(-8)
        .map((c) => `${c.role}: ${c.content}`)
        .join('\n');
      const context = `Engineering summary: ${summary}. Recent conversation:\n${tail}`;
      armHandoff('strategic-advisor', context, {
        firstMessage: 'Strategic Advisor back with you — where were we?',
      });
    },
    [armHandoff]
  );

  // ===========================================================================
  // (4) Conversation lifecycle.
  // ===========================================================================
  const startConversation = useCallback(
    async (persona, handoffContext, opts = {}) => {
      const control = controlRef.current;
      if (!control) {
        setStatus('Connect the control channel first.');
        return;
      }
      // Cancel any pending auto-restart so a queued reconnect can't race this
      // boot, and tear down a still-live Conversation first (duplicate-session
      // guard — no path ever runs two live Conversations at once).
      if (s2sRestartTimerRef.current) {
        clearTimeout(s2sRestartTimerRef.current);
        s2sRestartTimerRef.current = null;
      }
      if (conversationRef.current) {
        intentionalEndRef.current = true;
        await conversationRef.current.endSession().catch(() => {});
        conversationRef.current = null;
      }
      setShowRestart(false);
      setPersonaState('connecting');
      setS2sStatus('connecting');
      setStatus(`Connecting to ${PERSONA_LABELS[persona] || persona}…`);

      try {
        // (4) POST /s2s/token — x-firebase-token is required in ALLOWED_UIDS
        // mode and ignored by the middleware in localhost mode (W5). A handoff
        // passes a prefetched tokenPromise (minted at arm time); on rejection we
        // fall back to a fresh mint.
        const conversationToken = await (opts.tokenPromise
          ? opts.tokenPromise.catch(() => fetchS2SToken(persona))
          : fetchS2SToken(persona));

        const conversation = await Conversation.startSession({
          conversationToken,
          connectionType: 'webrtc',
          clientTools: buildClientTools({
            persona,
            userId: uid,
            getMemory,
            getProjects,
            control,
            callbacks: { onSummon, onReturn },
          }),
          // Per-session first_message override (enabled on both agents in W4):
          // reconnect/handoff sessions speak their own continuation line instead
          // of replaying the agent's canned greeting.
          overrides: opts.firstMessage ? { agent: { firstMessage: opts.firstMessage } } : undefined,
          dynamicVariables: {
            user_name: profileNameRef.current || 'there',
            handoff_context: handoffContext || '',
            active_jobs: jobsSummary(),
          },
          onConnect: () => {
            s2sRestartAttemptsRef.current = 0;
            setS2sStatus('connected');
            setPersonaState(persona);
            setStatus(`${PERSONA_LABELS[persona] || persona} is live.`);
          },
          onDisconnect: (details) => {
            conversationRef.current = null;
            // (8) Handoff-initiated (or End-button) ends never auto-restart —
            // the handoff's own startConversation owns the next session.
            if (intentionalEndRef.current) {
              intentionalEndRef.current = false;
              return;
            }
            if (unmountedRef.current) return;
            // Unexpected drop — up to 3 auto-restarts of the SAME persona.
            const attempt = s2sRestartAttemptsRef.current;
            setPersonaState('connecting');
            setS2sStatus('reconnecting');
            if (attempt >= S2S_RESTART_DELAYS_MS.length) {
              setS2sStatus('failed');
              setStatus('Voice connection lost. Click Restart.');
              setShowRestart(true);
              return;
            }
            const delay = S2S_RESTART_DELAYS_MS[attempt];
            s2sRestartAttemptsRef.current = attempt + 1;
            setStatus(
              `Voice dropped (${details?.reason || 'unknown'}) — restarting (${attempt + 1}/${S2S_RESTART_DELAYS_MS.length})…`
            );
            // Continuation context = last 10 CL turns, framed as a RECONNECT so
            // the persona resumes silently (its firstMessage override speaks the
            // only continuation line; the prompt suppresses re-greeting).
            const tail = (memoryRef.current?.CL || [])
              .slice(-10)
              .map((c) => `${c.role}: ${c.content}`)
              .join('\n');
            const ctx =
              'RECONNECT: the voice link dropped mid-conversation and is now restored. Recent transcript:\n' +
              tail +
              '\nContinue exactly where the conversation left off. Do NOT greet the user again or restate who you are.';
            s2sRestartTimerRef.current = setTimeout(() => {
              startConversationRef.current?.(persona, ctx, {
                firstMessage: 'Sorry — lost you for a second. You were saying?',
              });
            }, delay);
          },
          onError: (message) => {
            setStatus(`Voice error: ${message}`);
          },
          // (6) CL logging — ALL writes go through MemoryHighway. source 'user'
          // -> USER, 'ai' -> ASSISTANT. Entry shape {role,content,timestamp,
          // turnIndex} is unchanged; the content just carries an [S2S:<persona>]
          // prefix. Zero PMB/ECB writes anywhere in this component.
          onMessage: ({ message, source }) => {
            // The synthetic '[handoff-brief]' kickoff is fed via sendUserMessage
            // to make the specialist answer the pending request — it must NEVER
            // surface in the transcript pane or the conversation log.
            if (
              source === 'user' &&
              typeof message === 'string' &&
              message.startsWith(KICKOFF_PREFIX)
            ) {
              return;
            }
            const who = source === 'user' ? 'you' : 'agent';
            setTranscript((t) => [
              ...t,
              { who, persona: personaStateRef.current, text: message },
            ]);
            memoryRef.current?.appendToConversationLog(
              source === 'user' ? 'USER' : 'ASSISTANT',
              '[S2S:' + personaStateRef.current + '] ' + message
            );
          },
          // (5) Spoken-line-finished signal for handoffs: the SDK reports
          // 'speaking' while the agent voices its pre-handoff line, then
          // 'listening' when playback ends. The FIRST 'listening' after the ref
          // was armed fires executeHandoff. Does nothing while the ref is null.
          onModeChange: ({ mode }) => {
            if (mode === 'listening' && pendingHandoffRef.current) {
              executeHandoff();
            }
          },
        });

        conversationRef.current = conversation;
        // Re-apply mute state to a freshly booted session (mute survives handoffs).
        try {
          conversation.setMicMuted(muted);
        } catch {
          /* noop */
        }
        // Handoff kickoff: nudge the freshly-connected specialist to answer the
        // user's pending request immediately (filtered out of transcript/CL by
        // the onMessage guard above).
        if (opts.kickoff) {
          try {
            conversation.sendUserMessage(opts.kickoff);
          } catch {
            /* noop */
          }
        }
      } catch (e) {
        setPersonaState('idle');
        setS2sStatus('failed');
        setStatus(`Could not start voice: ${e.message}`);
        setShowRestart(true);
      }
    },
    [
      uid,
      getMemory,
      getProjects,
      fetchS2SToken,
      onSummon,
      onReturn,
      jobsSummary,
      executeHandoff,
      muted,
    ]
  );
  startConversationRef.current = startConversation;

  // ===========================================================================
  // (3) ControlChannel — created on Connect; wires W5 tunnel-mode auth.
  // ===========================================================================
  const connect = useCallback(() => {
    if (!token.trim()) {
      setStatus('Enter the agent token first.');
      return;
    }
    if (!controlRef.current) {
      const control = new ControlChannel({
        baseUrl: backendUrl.trim(),
        token: token.trim(),
        getIdToken: () => auth.currentUser.getIdToken(),
      });
      control.addEventListener('status', (e) => setControlStatus(e.detail));
      control.addEventListener('sessions', (e) => setJobs(e.detail || []));
      control.addEventListener('session-event', (e) => handleSessionEvent(e.detail));
      controlRef.current = control;
      control.connect();
      // Warm the 60s repo cache (fire-and-forget) so the first repo tool /
      // dispatch path resolution after a handoff to the engineer is instant.
      warmRepoCache(control);
      // Seed the jobs panel from the live session list.
      control
        .list()
        .then((sessions) => setJobs(sessions || []))
        .catch(() => {
          /* hello-ok will populate it anyway */
        });
    }
    // Start always boots the Strategic Advisor.
    startConversation('strategic-advisor', '');
  }, [backendUrl, token, handleSessionEvent, startConversation]);

  // (8) End button — intentional, suppresses the S2S auto-restart.
  const endConversation = useCallback(() => {
    if (s2sRestartTimerRef.current) {
      clearTimeout(s2sRestartTimerRef.current);
      s2sRestartTimerRef.current = null;
    }
    s2sRestartAttemptsRef.current = 0;
    setShowRestart(false);
    intentionalEndRef.current = true;
    const conv = conversationRef.current;
    conversationRef.current = null;
    if (conv) {
      conv.endSession().catch(() => {});
    }
    setPersonaState('idle');
    setS2sStatus('idle');
    setStatus('Conversation ended.');
  }, []);

  const restart = useCallback(() => {
    s2sRestartAttemptsRef.current = 0;
    setShowRestart(false);
    startConversation('strategic-advisor', '');
  }, [startConversation]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      try {
        conversationRef.current?.setMicMuted(next);
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  // (7) Permission decision — click-only. Resolves the gate via control.
  const respondPerm = useCallback((sessionId, id, decision) => {
    controlRef.current?.permission(sessionId, id, decision);
    setPerms((p) => p.filter((x) => x.id !== id));
  }, []);

  // ---- teardown on real unmount ----
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (s2sRestartTimerRef.current) clearTimeout(s2sRestartTimerRef.current);
      if (pendingHandoffRef.current?.timer) clearTimeout(pendingHandoffRef.current.timer);
      intentionalEndRef.current = true;
      conversationRef.current?.endSession().catch(() => {});
      conversationRef.current = null;
      controlRef.current?.close();
      controlRef.current = null;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Render (10) — header (persona chip + 3 status dots), transcript pane, right
  // rail (Jobs, Activity, perm cards), footer (Start/End, mute, config fields).
  // ---------------------------------------------------------------------------
  const dotClass = (s) =>
    s === 'connected' || s === 'ready'
      ? 'on'
      : s === 'connecting' || s === 'reconnecting' || s === 'loading'
        ? 'warn'
        : s === 'failed'
          ? 'err'
          : 'off';

  const live = personaState === 'strategic-advisor' || personaState === 'head-of-engineering';

  return (
    <div className="ac-root">
      <header className="ac-header">
        <h1>🛰️ Strategic Advisor</h1>
        <span className={`ac-chip ac-chip-${personaState}`}>
          {PERSONA_LABELS[personaState] || (personaState === 'connecting' ? 'Connecting…' : 'Idle')}
        </span>
        <div className="ac-dots">
          <span className="ac-dotlabel">
            <span className={`ac-dot ${dotClass(s2sStatus)}`} /> s2s
          </span>
          <span className="ac-dotlabel">
            <span className={`ac-dot ${dotClass(controlStatus)}`} /> control
          </span>
          <span className="ac-dotlabel">
            <span className={`ac-dot ${dotClass(memoryStatus)}`} /> memory
          </span>
        </div>
        <span className="ac-status">{status}</span>
      </header>

      {showRestart && (
        <div className="ac-connbar failed">
          <span>Voice connection lost.</span>
          <button className="ac-primary" onClick={restart}>
            Restart
          </button>
        </div>
      )}

      <div className="ac-body">
        <section className="ac-conversation">
          <h2>Conversation</h2>
          <div className="ac-scrollwrap">
            <div className="ac-log" ref={convScroll.containerRef}>
              {transcript.length === 0 && (
                <div className="ac-empty">Start the conversation to talk to the Advisor.</div>
              )}
              {transcript.map((m, i) => (
                <div key={i} className={`ac-msg ac-${m.who}`}>
                  <span className="ac-who">
                    {m.who === 'you'
                      ? 'You'
                      : PERSONA_LABELS[m.persona] || 'Advisor'}
                  </span>
                  <span className="ac-text">{m.text}</span>
                </div>
              ))}
            </div>
            {!convScroll.isPinned && (
              <button className="ac-jump" onClick={convScroll.jumpToLatest}>
                ↓ {convScroll.newCount > 0 ? `${convScroll.newCount} new` : 'Latest'}
              </button>
            )}
          </div>
        </section>

        <aside className="ac-rail">
          <section className="ac-jobs">
            <h2>Jobs</h2>
            {jobs.length === 0 && <div className="ac-empty">No active jobs.</div>}
            {jobs.map((j) => (
              <div key={j.sessionId} className="ac-job">
                <div className="ac-job-head">
                  <span className="ac-job-label">{j.label}</span>
                  {j.busy && <span className="ac-spinner" title="working" />}
                  {j.pendingPermissions > 0 && (
                    <span className="ac-badge" title="awaiting approval">
                      {j.pendingPermissions} ⚠️
                    </span>
                  )}
                </div>
                <div className="ac-job-repo">{j.repoName}</div>
              </div>
            ))}
          </section>

          {perms.length > 0 && (
            <section className="ac-perms">
              <h2>Approvals</h2>
              {perms.map((p) => (
                <div key={p.id} className="ac-perm">
                  <div className="ac-perm-title">⚠️ {p.title}</div>
                  <div className="ac-perm-job">{p.label}</div>
                  {p.command && <code className="ac-perm-cmd">{p.command}</code>}
                  {p.reason && <div className="ac-perm-reason">matched: {p.reason}</div>}
                  <div className="ac-perm-actions">
                    <button
                      className="ac-primary"
                      onClick={() => respondPerm(p.sessionId, p.id, 'allow')}
                    >
                      Allow
                    </button>
                    <button
                      className="ac-danger"
                      onClick={() => respondPerm(p.sessionId, p.id, 'deny')}
                    >
                      Deny
                    </button>
                  </div>
                </div>
              ))}
            </section>
          )}

          <section className="ac-activity">
            <h2>Activity</h2>
            <div className="ac-scrollwrap">
              <div className="ac-activity-log" ref={actScroll.containerRef}>
                {activity.items.length === 0 && <div className="ac-empty">No activity yet.</div>}
                {activity.items.map((a, i) => (
                  <div key={i} className="ac-action">
                    <span className="ac-action-label">{a.label}</span>
                    <span className="ac-action-text">{a.text}</span>
                  </div>
                ))}
              </div>
              {!actScroll.isPinned && (
                <button className="ac-jump" onClick={actScroll.jumpToLatest}>
                  ↓ {actScroll.newCount > 0 ? `${actScroll.newCount} new` : 'Latest'}
                </button>
              )}
            </div>
          </section>
        </aside>
      </div>

      <footer className="ac-footer">
        <div className="ac-field">
          <label>Backend</label>
          <input
            value={backendUrl}
            onChange={(e) => {
              setBackendUrl(e.target.value);
              persist('agentBackendUrl', e.target.value);
            }}
          />
        </div>
        <div className="ac-field">
          <label>Token</label>
          <input
            type="text"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={token}
            placeholder="AGENT_TOKEN"
            onChange={(e) => {
              const v = e.target.value.trim();
              setToken(v);
              persist('agentToken', v);
            }}
          />
        </div>
        {live || personaState === 'connecting' ? (
          <button className="ac-danger" onClick={endConversation}>
            End
          </button>
        ) : (
          <button className="ac-primary" onClick={connect} disabled={!token.trim()}>
            Start conversation
          </button>
        )}
        <button className="ac-ghost" onClick={toggleMute}>
          {muted ? '🔇 Muted' : '🎤 Mic on'}
        </button>
      </footer>
    </div>
  );
}
